import * as React from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Alert from '@amzn/awsui-components-console/alert';
import { useCollections } from '../../../HomePage/hooks/useCollections';
import ApplicationCollectionsTable from './ApplicationCollectionsTable';
import '../../ApplicationsPage.scss';

interface ManageApplicationModalProps {
  visible: boolean;
  applicationId: string;
  applicationName: string;
  subscribedCollectionUrls: string[];
  onDismiss: () => void;
  onConfirm: (selectedCollectionUrls: string[]) => void;
}

const ManageApplicationModal: React.FC<ManageApplicationModalProps> = ({
  visible,
  applicationName,
  subscribedCollectionUrls,
  onDismiss,
  onConfirm,
}) => {
  const [selectedCollectionUrls, setSelectedCollectionUrls] = React.useState<string[]>(subscribedCollectionUrls);
  const { collections, loading: collectionsLoading } = useCollections();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  // Update selected collections when prop changes
  React.useEffect(() => {
    setSelectedCollectionUrls(subscribedCollectionUrls);
  }, [subscribedCollectionUrls]);

  const handleConfirm = () => {
    setShowConfirmModal(true);
  };

  const handleFinalConfirm = () => {
    setShowConfirmModal(false);
    onConfirm(selectedCollectionUrls);
  };

  const handleDismiss = () => {
    // Reset to original values on dismiss
    setSelectedCollectionUrls(subscribedCollectionUrls);
    setShowConfirmModal(false);
    onDismiss();
  };

  // Check if there are any changes
  const hasChanges = React.useMemo(() => {
    if (selectedCollectionUrls.length !== subscribedCollectionUrls.length) {
      return true;
    }
    const sortedSelected = [...selectedCollectionUrls].sort();
    const sortedSubscribed = [...subscribedCollectionUrls].sort();
    return !sortedSelected.every((url, index) => url === sortedSubscribed[index]);
  }, [selectedCollectionUrls, subscribedCollectionUrls]);

  // Calculate subscribed and unsubscribed collections
  const getCollectionChanges = React.useMemo(() => {
    const newSubscriptions = selectedCollectionUrls.filter((url) => !subscribedCollectionUrls.includes(url));
    const unsubscriptions = subscribedCollectionUrls.filter((url) => !selectedCollectionUrls.includes(url));
    return { newSubscriptions, unsubscriptions };
  }, [selectedCollectionUrls, subscribedCollectionUrls]);

  // Get collection name from URL
  const getCollectionName = (url: string): string => {
    const collection = collections.find((c) => c.collectionId === url);
    return collection?.name || url;
  };

  return (
    <>
      <Modal
        visible={visible}
        onDismiss={handleDismiss}
        header={`Manage Content Collection Subscriptions - ${applicationName}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleDismiss}>
                Cancel
              </Button>
              <div className="bright-yellow-button">
                <Button variant="primary" onClick={handleConfirm} disabled={!hasChanges}>
                  {hasChanges
                    ? `Update Subscriptions (${getCollectionChanges.newSubscriptions.length + getCollectionChanges.unsubscriptions.length} changes)`
                    : 'No Changes'}
                </Button>
              </div>
            </SpaceBetween>
          </Box>
        }
        size="max"
      >
        <SpaceBetween size="l">
          <Alert type="info" header="Content Collection Subscriptions">
            Select which content collections this application should subscribe to. Subscribed collections will provide content data to your
            application. You can subscribe to new collections or unsubscribe from existing ones.
          </Alert>

          <Box>
            <Box variant="h3" margin={{ bottom: 'xs' }}>
              Current Status: {subscribedCollectionUrls.length} collection{subscribedCollectionUrls.length !== 1 ? 's' : ''} subscribed
            </Box>
            {hasChanges && (
              <Box color="text-status-info">
                <strong>Pending changes:</strong> {getCollectionChanges.newSubscriptions.length} new subscription
                {getCollectionChanges.newSubscriptions.length !== 1 ? 's' : ''}, {getCollectionChanges.unsubscriptions.length}{' '}
                unsubscription{getCollectionChanges.unsubscriptions.length !== 1 ? 's' : ''}
              </Box>
            )}
          </Box>

          <ApplicationCollectionsTable
            collections={collections}
            selectedCollectionUrls={selectedCollectionUrls}
            subscribedCollectionUrls={subscribedCollectionUrls}
            onSelectionChange={setSelectedCollectionUrls}
            loading={collectionsLoading}
          />
        </SpaceBetween>
      </Modal>

      <Modal
        visible={showConfirmModal}
        onDismiss={() => setShowConfirmModal(false)}
        header="Confirm Subscription Changes"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </Button>
              <div className="bright-yellow-button">
                <Button variant="primary" onClick={handleFinalConfirm}>
                  Apply Changes
                </Button>
              </div>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning" header="Review Your Changes">
            Please review the subscription changes below. These changes will take effect immediately and may impact your application's
            content access.
          </Alert>

          <Box>
            <Box variant="h4" margin={{ bottom: 's' }}>
              Application: <strong>{applicationName}</strong>
            </Box>
          </Box>

          {getCollectionChanges.newSubscriptions.length > 0 && (
            <Box>
              <Box variant="h4" margin={{ bottom: 'xs' }} color="text-status-success">
                ✓ New Subscriptions ({getCollectionChanges.newSubscriptions.length})
              </Box>
              <Box margin={{ left: 'm' }}>
                <em>These collections will be added to your application:</em>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {getCollectionChanges.newSubscriptions.map((url) => (
                    <li key={url} style={{ marginBottom: '4px' }}>
                      <strong>{getCollectionName(url)}</strong>
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>
          )}

          {getCollectionChanges.unsubscriptions.length > 0 && (
            <Box>
              <Box variant="h4" margin={{ bottom: 'xs' }} color="text-status-error">
                ✗ Unsubscriptions ({getCollectionChanges.unsubscriptions.length})
              </Box>
              <Box margin={{ left: 'm' }}>
                <em>These collections will be removed from your application:</em>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {getCollectionChanges.unsubscriptions.map((url) => (
                    <li key={url} style={{ marginBottom: '4px' }}>
                      <strong>{getCollectionName(url)}</strong>
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>
          )}

          {getCollectionChanges.newSubscriptions.length === 0 && getCollectionChanges.unsubscriptions.length === 0 && (
            <Box>
              <em>No changes detected.</em>
            </Box>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
};

export default ManageApplicationModal;
